import type { QuizEngine, QuestionTracker, StudyRecord } from "@shikaku/shared";
import { ChoiceButton } from "./ChoiceButton";
import { ExplanationCard } from "./ExplanationCard";
import { BookmarkButton } from "./BookmarkButton";

interface QuizViewProps {
  engine: QuizEngine;
  record: StudyRecord;
  tracker: QuestionTracker;
  accentColor: string;
  onFinish: () => void;
  onBack: () => void;
}

export function QuizView({
  engine, record, tracker, accentColor, onFinish, onBack,
}: QuizViewProps) {
  const q = engine.currentQuestion;
  const progress = engine.progress;
  const pct = Math.round(progress * 100);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#f5f5f5" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "12px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={onBack}
            style={{ color: accentColor, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}
          >
            ← 戻る
          </button>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>
            一問一答
          </span>
          <span style={{ fontSize: 13, color: "#888" }}>
            {engine.progressLabel}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 10, height: 8, background: "#e8e8e8", borderRadius: 4, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${accentColor}, #1abac4)`,
            borderRadius: 4,
            transition: "width 0.3s ease",
          }} />
        </div>
        <div style={{ marginTop: 4, textAlign: "right", fontSize: 12, fontWeight: 600, color: accentColor }}>
          {pct}%
        </div>
      </div>

      {/* Question body */}
      {q && (
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }} key={engine.currentIndex}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 8 }}>
            {q.unit}
          </div>
          <p style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.7, color: "#333", margin: "0 0 20px" }}>
            {q.question}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {q.choices.map((choice, i) => (
              <ChoiceButton
                key={i}
                text={choice}
                index={i}
                correctAnswer={q.answer}
                selectedAnswer={engine.selectedAnswer}
                onSelect={() =>
                  engine.selectAnswer(i, (correct) => record.recordAnswer(correct), tracker)
                }
              />
            ))}
          </div>

          {engine.isAnswered && (
            <div className="animate-fade-in" style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              <ExplanationCard text={q.explanation} accentColor={accentColor} />

              <button
                className="btn-primary"
                onClick={() => engine.isLastQuestion ? onFinish() : engine.nextQuestion()}
              >
                {engine.isLastQuestion ? "結果を見る" : "次の問題"}
              </button>

              <BookmarkButton tracker={tracker} questionId={q.id} accentColor={accentColor} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
